
import React, { useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface TeamSelectorProps {
  selectedTeamId: string;
  onSelectTeam: (teamId: string) => void;
}

export const TeamSelector: React.FC<TeamSelectorProps> = ({ 
  selectedTeamId, 
  onSelectTeam 
}) => {
  const { t } = useLanguage();
  
  // Fetch teams
  const { data: teams, isLoading } = useQuery({
    queryKey: ['teams', 'alerts', 'names'],
    staleTime: 60_000,
    gcTime: 300_000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .order('name');
        
      if (error) throw error;
      return data || [];
    }
  });
  
  const handleChange = (value: string) => {
    if (value === 'all') {
      onSelectTeam('');
    } else {
      onSelectTeam(value);
    }
  };
  
  return (
    <Select
      value={selectedTeamId || 'all'}
      onValueChange={handleChange}
      disabled={isLoading}
    >
      <SelectTrigger className="w-[180px]">
        <SelectValue placeholder={t('selectTeam') || 'Select team'} />
      </SelectTrigger>
      <SelectContent 
        className="bg-background max-h-[40vh] overflow-y-auto z-50"
        position="popper"
        side="bottom"
        align="start"
        sideOffset={8}
      >
        <SelectItem value="all">{t('allTeams') || 'All Teams'}</SelectItem>
        {teams?.map((team) => (
          <SelectItem key={team.id} value={team.id}>
            {team.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
